import { FC } from 'react';
import { Box, Theme, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { StyledLink, MenuBox } from '../style';
import whiteLogo from '../../../../media/whiteLogo.png';
import darkLogo from '../../../../media/blackLogo.png';
import '../logoStyles.css';
import MobileMenu from '../mobileMenu/MobileMenu';
import { useAppDispatch } from '../../../../hooks';
import useTranslations from '../../../global/translation/hooks/translation';
import { scrollFn } from '../helper';
import { useThemeContext } from '../../../global/ThemeContext';
import { setLanguage } from '../../../global/translation/translationSlice';
import DarkModeToggle from '../../DarkModeToggle';

const SecondAppBar: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const dispatch = useAppDispatch();

  // Place this component above SecondAppBar or in a separate file
  const MenuNavItem: FC<{ onClick: () => void; label: string; theme: Theme }> = ({ onClick, label, theme }) => (
    <Box
      sx={{
        cursor: 'pointer',
        mx: { md: 0.5, lg: 1 },
        color: darkMode ? '#FFFFFF' : '#000000',
        fontSize: { md: 14, lg: 16 },
        display: 'block',
        position: 'relative',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        px: { md: 0.5, lg: 1 },
        py: 1,
        whiteSpace: 'nowrap',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 0,
          height: '2px',
          backgroundColor: darkMode ? '#FFFFFF' : '#000000',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover': {
          '&::after': {
            width: '100%',
          },
        },
      }}
      onClick={onClick}
    >
      {label}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          px: { xs: 2, md: 3, lg: 20 },
          background: darkMode ? 'rgba(24, 24, 24, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          color: darkMode ? '#FFFFFF' : '#000000',
          boxShadow: darkMode ? 'none' : '0px 2px 4px rgba(0, 0, 0, 0.05)',
        }}
      >
        <MobileMenu />
        <MenuBox
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Logo on the left */}
          <Box sx={{ flex: '0 0 auto', pr: { md: 1, lg: 2 } }}>
            <StyledLink to={'/'} onClick={() => scrollFn('top')}>
              <img
                style={{
                  scale: '0.7',
                }}
                alt="urban_equestrian"
                src={darkMode ? whiteLogo : darkLogo}
                className="whiteLogoStyles"
              />
            </StyledLink>
          </Box>
          {/* Menu items in the center */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { md: 0.5, lg: 2 }, flex: 1, justifyContent: 'center' }}>
            {[
              { key: 'aboutUs', section: 'about', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'offer', section: 'offer', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'horses', section: 'horses', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'method', section: 'method', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'pricing', section: 'pricing', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'ourTeam', section: 'team', icon: <span style={{ marginRight: 4 }}></span> },
              { key: 'contact', section: 'contact', icon: <span style={{ marginRight: 4 }}></span> },
            ].map(({ key, section, icon }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                {icon}
                <MenuNavItem onClick={() => scrollFn(section)} label={translations.menuItems[key][currentLanguage]} theme={theme} />
              </Box>
            ))}
          </Box>
          {/* Theme, language, and CTA button on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { md: 1, lg: 2 }, flex: '0 0 auto' }}>
            <DarkModeToggle />
            <Button
              onClick={() => {
                currentLanguage === 'en' ? dispatch(setLanguage('pl')) : dispatch(setLanguage('en'));
              }}
              size="medium"
              sx={{ minWidth: 0, px: { md: 1.5, lg: 2 }, fontWeight: 600, color: darkMode ? '#FFFFFF' : '#000000' }}
            >
              {currentLanguage === 'en' ? 'PL' : 'EN'}
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={() => (window.location.href = 'https://lubie.urbanequestrian.pl/')}
              sx={{
                ml: { md: 0.5, lg: 1 },
                bgcolor: theme.palette.primary.main,
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: { md: '13px', lg: '14px' },
                textTransform: 'none',
                px: { md: 2, lg: 3 },
                py: 1,
                borderRadius: '4px',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {translations.label.ctaApplyTraining[currentLanguage]}
            </Button>
          </Box>
        </MenuBox>
      </Box>
    </>
  );
};

export default SecondAppBar;
